"use client";

import { flattenCategories } from "@/lib/helpers/categoryHelpers";
import { CardsList } from "@/lib/interfaces/cards";
import { CategoryList } from "@/lib/interfaces/categories";
import { CardService } from "@/lib/services/cardService";
import { CategoryService } from "@/lib/services/categoryService";
import Image from "next/image";
import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ArrowDown, Check, Loader2, X } from "lucide-react";

interface BatchCardsSheetProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    categorySlug: string;
    onSelect: (cards: CardsList[]) => void;
    initialSelectedCards?: CardsList[];
}

/**
 * Design picker for bulk orders. The API pages designs 20 at a time, so the
 * grid uses 2 / 4 / 5 columns: every page fills complete rows before "Load more".
 */
const BatchCardsSheet: React.FC<BatchCardsSheetProps> = ({
    isOpen,
    onClose,
    title,
    categorySlug,
    onSelect,
    initialSelectedCards = [],
}) => {
    const [selectedCategory, setSelectedCategory] = useState<string>(categorySlug);
    const [cards, setCards] = useState<CardsList[]>([]);
    const [selectedCards, setSelectedCards] = useState<CardsList[]>(initialSelectedCards);
    const [categories, setCategories] = useState<CategoryList[]>([]);
    const [mounted, setMounted] = useState(false);

    // Drag-to-scroll for the category chips
    const scrollRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const dragMoved = useRef(false);

    // The API returns one page of designs at a time; "Load more" appends the next page.
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [totalCount, setTotalCount] = useState<number | null>(null);
    // Ignore responses for a category the user has already switched away from.
    const activeSlugRef = useRef(categorySlug);

    useEffect(() => setMounted(true), []);

    // Sync selection whenever the sheet opens
    useEffect(() => {
        if (isOpen) {
            setSelectedCards(initialSelectedCards || []);
        }
    }, [isOpen, initialSelectedCards]);

    // Lock page scroll and close on Escape while open
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prev;
        };
    }, [isOpen, onClose]);

    async function getCardsData(slug: string, pageToLoad = 1) {
        activeSlugRef.current = slug;
        setIsLoading(true);
        try {
            const cardsRes = await CardService.getCardsBySlug(slug, {
                filters: '',
                is_latest: true,
                is_staff_pick: false,
                sort_by: 'position',
                sort_order: 'asc',
                page: pageToLoad,
            });
            if (activeSlugRef.current !== slug) return;
            if (cardsRes?.response_status && cardsRes?.response_data) {
                const pageCards = cardsRes.response_data as CardsList[];
                setCards(prev => {
                    if (pageToLoad === 1) return pageCards;
                    const seen = new Set(prev.map(c => c.id));
                    return [...prev, ...pageCards.filter(c => !seen.has(c.id))];
                });
                setPage(pageToLoad);
                setHasMore(Boolean(cardsRes.pagination?.next) && pageCards.length > 0);
                setTotalCount(typeof cardsRes.pagination?.count === 'number' ? cardsRes.pagination.count : null);
            } else {
                if (pageToLoad === 1) setCards([]);
                setHasMore(false);
            }
        } catch (error) {
            console.error('Error fetching cards:', error);
            setHasMore(false);
        } finally {
            if (activeSlugRef.current === slug) setIsLoading(false);
        }
    }

    const loadMore = () => {
        if (isLoading || !hasMore) return;
        getCardsData(activeSlugRef.current, page + 1);
    };

    useEffect(() => {
        getCardsData(categorySlug);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const result = await CategoryService.getCategories();
                setCategories(result.response_status && result.response_data ? flattenCategories(result.response_data) : []);
            } catch (error) {
                console.error('Error fetching categories:', error);
                setCategories([]);
            }
        };
        fetchCategories();
    }, []);

    const selectCategory = (slug: string) => {
        if (dragMoved.current || slug === selectedCategory) return;
        setSelectedCategory(slug);
        setCards([]);
        setTotalCount(null);
        getCardsData(slug, 1);
    };

    const toggleCardSelection = (card: CardsList) => {
        setSelectedCards(prev =>
            prev.some(c => c.id === card.id) ? prev.filter(c => c.id !== card.id) : [...prev, card]
        );
    };

    const handleDone = () => {
        onSelect(selectedCards);
        onClose();
    };

    if (!isOpen || !mounted) return null;

    const activeCategory = categories.find(c => c.category_slug === selectedCategory);

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-6" role="presentation">
            <div className="absolute inset-0 bg-ink/50 backdrop-blur-[2px] animate-in fade-in duration-200" onClick={onClose} aria-hidden="true" />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="design-picker-title"
                className="relative flex h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-t-3xl bg-paper shadow-[0_30px_80px_-30px_rgba(6,35,63,0.55)] animate-in slide-in-from-bottom-6 fade-in duration-300 sm:h-[88vh] sm:rounded-3xl"
            >
                {/* Header */}
                <div className="border-b border-paper-rule bg-white px-5 pb-4 pt-5 sm:px-7">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <h2 id="design-picker-title" className="font-display text-2xl text-ink">{title}</h2>
                            <p className="mt-0.5 text-sm text-gray-600">
                                Pick one or more. Numbers show the order designs are handed out down your list.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close"
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-600 transition-colors hover:bg-paper hover:text-ink"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    <div
                        ref={scrollRef}
                        className={`-mx-1 mt-4 flex gap-2 overflow-x-auto px-1 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${isDragging ? 'cursor-grabbing select-none' : 'cursor-grab'}`}
                        onMouseDown={(e) => {
                            if (!scrollRef.current) return;
                            setIsDragging(true);
                            dragMoved.current = false;
                            setStartX(e.pageX - scrollRef.current.offsetLeft);
                            setScrollLeft(scrollRef.current.scrollLeft);
                        }}
                        onMouseMove={(e) => {
                            if (!isDragging || !scrollRef.current) return;
                            e.preventDefault();
                            const walk = (e.pageX - scrollRef.current.offsetLeft - startX) * 1.5;
                            if (Math.abs(walk) > 5) dragMoved.current = true;
                            scrollRef.current.scrollLeft = scrollLeft - walk;
                        }}
                        onMouseUp={() => { setIsDragging(false); setTimeout(() => { dragMoved.current = false; }, 0); }}
                        onMouseLeave={() => setIsDragging(false)}
                        role="tablist"
                        aria-label="Card categories"
                    >
                        {categories.map((category) => {
                            const active = selectedCategory === category.category_slug;
                            return (
                                <button
                                    key={category.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={active}
                                    onClick={() => selectCategory(category.category_slug)}
                                    className={`shrink-0 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                                        active ? 'bg-ink text-white' : 'border border-paper-rule bg-white text-gray-700 hover:border-gray-300 hover:text-ink'
                                    }`}
                                >
                                    {category.category_name}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Grid */}
                <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-7">
                    {activeCategory && (
                        <p className="mb-4 text-sm text-gray-600">
                            <span className="font-semibold text-ink">{activeCategory.category_name}</span>
                            {totalCount !== null && <> · {totalCount} designs</>}
                        </p>
                    )}

                    {cards.length === 0 && isLoading ? (
                        <ul className="grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-5 lg:grid-cols-5" aria-hidden="true">
                            {Array.from({ length: 10 }).map((_, i) => (
                                <li key={i} className="aspect-[5/7] animate-pulse rounded-xl bg-paper-linen" />
                            ))}
                        </ul>
                    ) : cards.length === 0 ? (
                        <p className="py-16 text-center text-gray-600">No designs in this category yet.</p>
                    ) : (
                        <ul className="grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-5 lg:grid-cols-5">
                            {cards.map((card) => {
                                const order = selectedCards.findIndex((c) => c.id === card.id);
                                const isSelected = order !== -1;
                                return (
                                    <li key={card.id}>
                                        <button
                                            type="button"
                                            onClick={() => toggleCardSelection(card)}
                                            aria-pressed={isSelected}
                                            aria-label={`${card.name}${isSelected ? `, selected ${order + 1}` : ''}`}
                                            className={`group relative block aspect-[5/7] w-full overflow-hidden rounded-xl bg-white transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2 ${
                                                isSelected
                                                    ? 'shadow-[0_14px_30px_-16px_rgba(6,35,63,0.6)] ring-[3px] ring-ink'
                                                    : 'ring-1 ring-paper-rule hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-18px_rgba(6,35,63,0.5)]'
                                            }`}
                                        >
                                            <Image
                                                src={card.card_image}
                                                alt=""
                                                fill
                                                sizes="(min-width: 1024px) 20vw, (min-width: 640px) 25vw, 50vw"
                                                className="object-cover"
                                            />
                                            <span
                                                className={`absolute right-2 top-2 flex h-7 min-w-[1.75rem] items-center justify-center rounded-full px-1.5 text-xs font-semibold shadow-sm transition-colors ${
                                                    isSelected ? 'bg-ink text-white' : 'border border-white/80 bg-white/80 text-transparent group-hover:text-gray-400'
                                                }`}
                                                aria-hidden="true"
                                            >
                                                {isSelected ? order + 1 : <Check className="h-3.5 w-3.5" />}
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {(hasMore || (isLoading && cards.length > 0)) && (
                        <div className="mt-8 flex flex-col items-center gap-2 pb-2">
                            <button
                                type="button"
                                onClick={loadMore}
                                disabled={isLoading}
                                aria-busy={isLoading}
                                className="inline-flex items-center gap-2 rounded-full border border-paper-rule bg-white px-6 py-3 text-sm font-semibold text-ink shadow-sm transition-colors hover:border-ink disabled:opacity-70"
                            >
                                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <ArrowDown className="h-4 w-4" aria-hidden="true" />}
                                {isLoading ? 'Loading designs…' : 'Load more designs'}
                            </button>
                            {totalCount !== null && (
                                <p className="text-xs text-gray-600">Showing {cards.length} of {totalCount}</p>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-4 border-t border-paper-rule bg-white px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-7">
                    <div className="flex min-w-0 items-center gap-3">
                        {selectedCards.length > 0 ? (
                            <>
                                <div className="hidden -space-x-3 sm:flex">
                                    {selectedCards.slice(0, 5).map((c) => (
                                        // eslint-disable-next-line @next/next/no-img-element
                                        <img key={c.id} src={c.card_image} alt="" className="h-11 w-8 rounded-md border-2 border-white object-cover shadow-sm" />
                                    ))}
                                </div>
                                <p className="text-sm text-gray-700">
                                    <span className="font-semibold text-ink">{selectedCards.length}</span> selected
                                </p>
                                <button type="button" onClick={() => setSelectedCards([])} className="text-sm font-medium text-gray-600 underline underline-offset-4 hover:text-ink">
                                    Clear
                                </button>
                            </>
                        ) : (
                            <p className="text-sm text-gray-600">No designs selected yet</p>
                        )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                        <button type="button" onClick={onClose} className="hidden rounded-full px-4 py-2.5 text-sm font-semibold text-gray-700 hover:text-ink sm:block">
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleDone}
                            className="rounded-full bg-ink px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-ink-700 disabled:opacity-50"
                        >
                            {selectedCards.length > 0
                                ? `Add ${selectedCards.length} ${selectedCards.length === 1 ? 'design' : 'designs'}`
                                : 'Done'}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default BatchCardsSheet;
